/* eslint-disable @typescript-eslint/no-explicit-any */
import { LookupModel, Pagination } from "app/shared/models";
import { BaseCRUD } from "app/shared/services/base-crud.service";
import { debounceTime, Subject, take, takeUntil, tap } from "rxjs";

import { Overlay, OverlayRef } from "@angular/cdk/overlay";
import { TemplatePortal } from "@angular/cdk/portal";
import { NgFor, NgIf } from "@angular/common";
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
} from "@angular/core";
import {
  AbstractControl,
  ControlValueAccessor,
  FormsModule,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidationErrors,
  Validator,
  Validators,
} from "@angular/forms";
import { MatRippleModule } from "@angular/material/core";
import { MatError, MatFormField, MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatTooltipModule } from "@angular/material/tooltip";
import { TranslocoModule } from "@jsverse/transloco";

@Component({
  selector: "app-lookup",
  templateUrl: "./ezv-lookup.component.html",
  styleUrls: ["./ezv-lookup.component.scss"],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  exportAs: "lookup",
  imports: [
    TranslocoModule,
    NgIf,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    NgFor,
    MatError,
    MatRippleModule,
    MatTooltipModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: EzvLookupComponent,
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: EzvLookupComponent,
    },
  ],
})
export class EzvLookupComponent<T>
  implements OnInit, AfterViewInit, OnChanges, OnDestroy, ControlValueAccessor, Validator
{
  @Input() entity: LookupModel | null | undefined;
  @Input() label: string = "";
  @Input() errorMessage: string | null | undefined;
  @Input() service: BaseCRUD<T> | null = null;
  @Input() required: boolean = false;
  @Input() editing: boolean = false;
  @Input() showLabel = true;
  @Input() rounded = false;
  @Input() clearable = false;
  @Input() loadOnOpen = false;
  @Output() update = new EventEmitter<LookupModel>();

  @ViewChild("formField") private _formField: MatFormField | null = null;
  @ViewChild("panel") private _panel: TemplateRef<any> | null = null;
  @ViewChild("panelOrigin") private _panelOrigin: ElementRef | null = null;

  private _changeDetectorRef: ChangeDetectorRef;
  private _overlay: Overlay;
  private _panelOverlayRef: OverlayRef | null = null;
  private _renderer2: Renderer2;
  private _unsubscribeAll: Subject<any> = new Subject<any>();
  private _viewContainerRef: ViewContainerRef;
  private _debounceSubject: Subject<string> = new Subject();
  private _formBuilder: UntypedFormBuilder;

  form: UntypedFormGroup | null = null;
  formFieldWidth: number = 0;
  disabled = true;
  touched = false;
  invalid = false;
  filteredData: LookupModel[] = [];
  activeOptionIndex = -1;
  searched = false;

  constructor() {
    this._changeDetectorRef = inject(ChangeDetectorRef);
    this._overlay = inject(Overlay);
    this._renderer2 = inject(Renderer2);
    this._viewContainerRef = inject(ViewContainerRef);
    this._formBuilder = inject(UntypedFormBuilder);
  }

  ngOnInit(): void {
    if (this.required) {
      this.form = this._formBuilder.group({
        description: [this.entity?.description, [Validators.required]],
      });
    } else {
      this.form = this._formBuilder.group({
        description: [this.entity?.description],
      });
    }

    this.syncDisabledState();

    this._debounceSubject
      .pipe(debounceTime(800), takeUntil(this._unsubscribeAll))
      .subscribe((value) => this.search(value));
  }

  private search(value: string): void {
    this.service!.findAll(
      {
        size: 100,
        page: 0,
        // sort: "codigo",
        sortOrder: "ASC",
      } as Pagination,
      value,
    )
      .pipe(
        take(1),
        tap((response) => {
          this.filteredData =
            response.data?.map((item: T) => {
              return {
                id: item["id" as keyof T] as string,
                description: Array.isArray(this.service!.descriptionField)
                  ? this.service!.descriptionField.map((field) => item[field as keyof T] as string).join(" - ")
                  : (item[this.service!.descriptionField as keyof T] as string),
                tooltip: this.service!.tooltipField ? (item[this.service!.tooltipField as keyof T] as string) : null,
              } as LookupModel;
            }) ?? [];

          this.activeOptionIndex = this.filteredData.length > 0 ? 0 : -1;
          this.searched = true;

          this._changeDetectorRef.markForCheck();
        }),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();

    if (this._panelOverlayRef) {
      this._panelOverlayRef.dispose();
    }
  }

  ngAfterViewInit(): void {
    this._changeDetectorRef.markForCheck();

    this.formFieldWidth = this._formField?._elementRef.nativeElement.offsetWidth || 0;

    this.onChange(this.entity ?? null);
  }

  ngOnChanges(): void {
    this.syncDisabledState();

    this.form?.patchValue({ description: this.entity?.description ?? "" }, { emitEvent: false });

    this.onChange(this.entity ?? null);
  }

  private syncDisabledState(): void {
    if (!this.form) {
      return;
    }

    if (this.editing) {
      this.form.disable({ emitEvent: false });
    } else {
      this.form.enable({ emitEvent: false });
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private onChange = (_: LookupModel | null) => {
    this.checkValue(this.entity?.description);
  };

  private onTouched = () => {};

  private markAsTouched() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }

    this.checkValue(this.entity?.description);

    this._changeDetectorRef.markForCheck();
  }

  private checkValue(value: any): ValidationErrors | null {
    this.invalid = false;

    if (!this.required) {
      return null;
    }

    if (!value) {
      this.invalid = true;

      return {
        required: {
          value,
        },
      };
    }

    return null;
  }

  private closePartnersPanel(templatePortal?: TemplatePortal) {
    this._renderer2.removeClass(this._panelOrigin?.nativeElement, "panel-opened");

    if (this._panelOverlayRef && this._panelOverlayRef.hasAttached()) {
      this._panelOverlayRef.detach();
      this.filteredData = [];
      this.activeOptionIndex = -1;
      this.searched = false;
    }

    if (templatePortal && templatePortal.isAttached) {
      templatePortal.detach();
    }
  }

  writeValue(obj: LookupModel): void {
    this.entity = obj;
  }

  registerOnChange(onChange: any): void {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: any): void {
    this.onTouched = onTouched;
  }

  openPanel(event: KeyboardEvent | MouseEvent): void {
    if (this.editing) return;

    if (event instanceof KeyboardEvent && event.key === "Tab") {
      event.preventDefault();
      this.closePartnersPanel();
      this.focusAdjacentElement(event.shiftKey ? -1 : 1);
      return;
    }

    if (this._panelOverlayRef && this._panelOverlayRef.hasAttached()) {
      return;
    }

    // Mede na abertura: no ngAfterViewInit o layout da aba ainda pode não estar final.
    this.formFieldWidth = this._formField?._elementRef.nativeElement.offsetWidth || this.formFieldWidth;

    this._panelOverlayRef = this._overlay.create({
      backdropClass: "",
      hasBackdrop: true,
      scrollStrategy: this._overlay.scrollStrategies.block(),
      positionStrategy: this._overlay
        .position()
        .flexibleConnectedTo(this._panelOrigin?.nativeElement)
        .withFlexibleDimensions(true)
        .withViewportMargin(64)
        .withLockedPosition(true)
        .withPositions([
          {
            originX: "start",
            originY: "bottom",
            overlayX: "start",
            overlayY: "top",
          },
        ]),
    });

    this._panelOverlayRef.attachments().subscribe(() => {
      this._renderer2.addClass(this._panelOrigin?.nativeElement, "panel-opened");
      this._panelOverlayRef?.overlayElement.querySelector("input")?.focus();
    });

    if (this._panel) {
      const templatePortal = new TemplatePortal(this._panel, this._viewContainerRef);
      this._panelOverlayRef.attach(templatePortal);
      this._panelOverlayRef.backdropClick().subscribe(() => {
        this.closePartnersPanel(templatePortal);
      });
    }

    setTimeout(() => {
      this._panelOverlayRef?.overlayElement.querySelector("input")?.focus();
    }, 100);

    if (this.loadOnOpen) {
      this.search("");
    }
  }

  filterData(event: any): void {
    const value = event.target.value.toLowerCase();
    this._debounceSubject.next(value);
  }

  onSearchKeydown(event: KeyboardEvent): void {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      this.moveActiveOption(1);
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      this.moveActiveOption(-1);
      return;
    }

    if (event.key === "Enter") {
      event.preventDefault();
      this.selectActiveOption();
      return;
    }

    if (event.key === "Tab") {
      event.preventDefault();
      this.closePartnersPanel();
      this.focusAdjacentElement(event.shiftKey ? -1 : 1);
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      this.closePartnersPanel();
    }
  }

  setActiveOption(index: number): void {
    if (!this.filteredData.length) {
      this.activeOptionIndex = -1;
      return;
    }

    if (index < 0) {
      this.activeOptionIndex = 0;
    } else if (index >= this.filteredData.length) {
      this.activeOptionIndex = this.filteredData.length - 1;
    } else {
      this.activeOptionIndex = index;
    }

    this.scrollActiveOptionIntoView();
    this._changeDetectorRef.markForCheck();
  }

  private moveActiveOption(step: number): void {
    if (!this.filteredData.length) {
      return;
    }

    const nextIndex = this.activeOptionIndex < 0 ? 0 : this.activeOptionIndex + step;
    const wrappedIndex = (nextIndex + this.filteredData.length) % this.filteredData.length;

    this.setActiveOption(wrappedIndex);
  }

  private selectActiveOption(): void {
    if (this.activeOptionIndex < 0 || this.activeOptionIndex >= this.filteredData.length) {
      return;
    }

    this.select(this.filteredData[this.activeOptionIndex]);
  }

  private scrollActiveOptionIntoView(): void {
    setTimeout(() => {
      const activeElement = this._panelOverlayRef?.overlayElement.querySelector(
        `[data-lookup-index="${this.activeOptionIndex}"]`,
      );

      activeElement?.scrollIntoView({ block: "nearest" });
    });
  }

  select(partner: LookupModel): void {
    this.entity = partner;
    this.form?.patchValue(partner);

    this.closePartnersPanel();

    this.writeValue(this.entity);

    this.onChange(this.entity);

    this.markAsTouched();

    if (this.update) {
      this.update.emit(this.entity);
    }

    this.focusTriggerInput();
  }

  clear(event: MouseEvent): void {
    event.stopPropagation();

    if (this.editing) return;

    this.select({ id: null, description: "" } as LookupModel);
  }

  private focusTriggerInput(): void {
    const triggerInput = this._panelOrigin?.nativeElement.querySelector("input");
    triggerInput?.focus();
  }

  private focusAdjacentElement(direction: 1 | -1): void {
    const current = this._panelOrigin?.nativeElement.querySelector("input") as HTMLElement | null;
    if (!current) {
      return;
    }

    const focusableElements = Array.from(
      document.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ),
    ).filter((element) => !element.hasAttribute("disabled") && element.tabIndex >= 0);

    const currentIndex = focusableElements.indexOf(current);
    if (currentIndex < 0) {
      return;
    }

    const nextIndex = currentIndex + direction;
    const nextElement = focusableElements[nextIndex];
    nextElement?.focus();
  }

  trackByFn(index: number, item: any): any {
    return item.id || index;
  }

  validate(control: AbstractControl): ValidationErrors | null {
    return this.checkValue(control.value);
  }
}
